/**
 * Inline "Thinking" terminal for the agentic-room turn card.
 *
 * Visual contract:
 *   - **Live turn** → 5-line tail with a faint top-fade (older lines feel
 *     like they're scrolling away), a soft pulse on the latest line, and
 *     elapsed time. Click anywhere on the chrome to expand the full
 *     transcript.
 *   - **Closed turn** → a single thin caption row ("Thinking ✓ N steps ·
 *     X.Xs"), still clickable to re-expand. This keeps history readable
 *     without clogging the feed.
 *
 * Why it's stateful but never "owns" the lines:
 *   The reducer is the source of truth for what's in the transcript.
 *   This component only stores ephemeral UI state (expanded vs. collapsed
 *   for closed turns; auto-tail follow for the live one).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Brain, ChevronDown, ChevronUp, Check, AlertTriangle, Loader2 } from 'lucide-react';

const KIND_ICONS = {
  memory: '🧠',
  agent: '🤖',
  llm: '✦',
  tool: '🛠',
  doc: '📄',
  status: '•',
  system: '⚙',
  reasoning: '💭',
  unknown: '·',
};

const KIND_TONE = {
  memory: 'text-violet-700',
  agent: 'text-sky-700',
  llm: 'text-indigo-700',
  tool: 'text-amber-700',
  doc: 'text-emerald-700',
  status: 'text-slate-700',
  system: 'text-slate-700',
  reasoning: 'text-fuchsia-700',
  unknown: 'text-slate-600',
};

const TAIL_LINES = 5;

function formatElapsed(startedAt, endedAt) {
  if (!startedAt) return '';
  const a = typeof startedAt === 'string' ? Date.parse(startedAt) : Number(startedAt);
  const b = endedAt
    ? typeof endedAt === 'string' ? Date.parse(endedAt) : Number(endedAt)
    : Date.now();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return '';
  const ms = b - a;
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s - m * 60);
  return `${m}m ${rem}s`;
}

/** Re-render every 1s while the turn is live so the elapsed counter ticks. */
function useLiveClock(active) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [active]);
}

function StatusPill({ status }) {
  if (status === 'live') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
        <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden />
        thinking
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700">
        <AlertTriangle className="h-2.5 w-2.5" aria-hidden />
        failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
      <Check className="h-2.5 w-2.5" aria-hidden />
      done
    </span>
  );
}

function ThinkingRow({ line, isLatest, dimmed }) {
  const tone = KIND_TONE[line.kind] || KIND_TONE.unknown;
  const icon = KIND_ICONS[line.kind] || KIND_ICONS.unknown;
  // Reasoning lines carry a full sentence in ``detail`` — let it WRAP so the whole thought is
  // readable, instead of clipping to one truncated line like terse status/tool rows do.
  const isReasoning = line.kind === 'reasoning';
  return (
    <div
      className={`flex min-w-0 gap-2 font-mono text-[12px] leading-5 transition-opacity ${
        isReasoning ? 'items-start' : 'items-baseline'
      } ${dimmed ? 'opacity-50' : 'opacity-100'}`}
    >
      <span className={`shrink-0 ${tone}`} aria-hidden>{icon}</span>
      <span className={`min-w-0 flex-1 ${isReasoning ? 'break-words whitespace-pre-wrap' : 'truncate'}`}>
        <span className={`font-medium ${tone}`}>{line.label}</span>
        {line.detail ? (
          <span className="ml-2 text-slate-500">{line.detail}</span>
        ) : null}
        {line.agentId ? (
          <span className="ml-2 rounded bg-slate-100 px-1 py-px text-[10px] uppercase tracking-wide text-slate-600">
            {line.agentId}
          </span>
        ) : null}
      </span>
      {isLatest ? (
        <span className="ml-2 inline-block h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-blue-500" aria-hidden />
      ) : null}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {{
 *   thinking: import('@/utils/thinkingLine.js').ThinkingLine[],
 *   status: 'live'|'completed'|'failed',
 *   activeAgentId?: string,
 *   startedAt?: string|number,
 *   endedAt?: string|number,
 * }} props.turn
 */
export default function ThinkingTerminal({ turn }) {
  const { thinking, status, activeAgentId, startedAt, endedAt } = turn;
  const isLive = status === 'live';
  const [expanded, setExpanded] = useState(false);
  // Auto-collapse the closed-turn caption back to a one-liner whenever the
  // turn flips from live → completed; users can still click to re-open.
  useEffect(() => {
    if (!isLive) setExpanded(false);
  }, [isLive]);

  useLiveClock(isLive);

  const visible = useMemo(() => {
    if (expanded) return thinking;
    if (!isLive) return [];
    return thinking.slice(-TAIL_LINES);
  }, [expanded, isLive, thinking]);

  const stepCount = thinking.length;
  const elapsed = formatElapsed(startedAt, endedAt);

  // Closed turn — single caption row, no fancy chrome unless expanded.
  if (!isLive && !expanded) {
    if (stepCount === 0) return null;
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="group inline-flex items-center gap-2 self-start rounded-md border border-slate-200 bg-white/70 px-2 py-1 text-[11px] text-slate-600 transition-colors hover:border-slate-300 hover:bg-white"
        aria-label="Show full thinking transcript"
      >
        <Brain className="h-3 w-3 text-slate-500" aria-hidden />
        <span className="font-medium">Thinking</span>
        <StatusPill status={status} />
        <span className="text-slate-500">
          {stepCount} step{stepCount === 1 ? '' : 's'}
          {elapsed ? ` · ${elapsed}` : ''}
        </span>
        <ChevronDown className="h-3 w-3 text-slate-400 transition-transform group-hover:translate-y-px" aria-hidden />
      </button>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50/80 shadow-sm">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-white/60 px-3 py-1.5">
        <Brain className={`h-3.5 w-3.5 ${isLive ? 'animate-pulse text-blue-600' : 'text-slate-500'}`} aria-hidden />
        <span className="text-xs font-semibold text-slate-700">Thinking</span>
        <StatusPill status={status} />
        {activeAgentId ? (
          <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
            {activeAgentId}
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-2 text-[11px] text-slate-500">
          {elapsed ? <span>{elapsed}</span> : null}
          <span>·</span>
          <span>
            {stepCount} step{stepCount === 1 ? '' : 's'}
          </span>
          {stepCount > 0 ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-slate-600 hover:bg-slate-100"
              aria-label={expanded ? 'Show tail only' : 'Show full transcript'}
            >
              {expanded ? <ChevronUp className="h-3 w-3" aria-hidden /> : <ChevronDown className="h-3 w-3" aria-hidden />}
              {expanded ? 'Tail' : 'All'}
            </button>
          ) : null}
        </div>
      </div>
      <div className="relative">
        {!expanded && isLive && stepCount > TAIL_LINES ? (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-slate-50 to-transparent" />
        ) : null}
        <div
          className={`flex min-w-0 flex-col gap-1 px-3 py-2 ${
            expanded ? 'max-h-72 overflow-y-auto' : ''
          }`}
        >
          {visible.length === 0 ? (
            <div className="font-mono text-[12px] leading-5 text-slate-500">
              {isLive ? 'Listening for the agent…' : 'No transcript captured.'}
            </div>
          ) : null}
          {visible.map((line, idx) => (
            <ThinkingRow
              key={line.id}
              line={line}
              isLatest={isLive && idx === visible.length - 1}
              dimmed={!expanded && isLive && idx < visible.length - 1 && idx === 0}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
